import {
  FormControl,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Checkbox, FilterLabel } from '@retail/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useMemo } from 'react';
import { SubAssortmentChip } from '../SubAssortmentChip';

interface Props {
  includeUnselectedLevel?: boolean;
  placeholder?: string;
  assortmentTags: AssortmentTag[];
  selectedTags: AssortmentTag[] | null;
  selectTagById: (tagId: number) => void;
  resetTags: () => void;
}

export function AssortmentTagSelect({
  includeUnselectedLevel = true,
  placeholder,
  assortmentTags,
  selectedTags,
  selectTagById,
  resetTags,
}: Props) {
  const t = useMyAssortmentTFunction();
  const placeholderText = placeholder ?? t('myAssortment.filters.filterSubAssortments');
  const selectedTagIds = useMemo(() => selectedTags?.map((tag) => tag.id) ?? [], [selectedTags]);

  return (
    <FormControl sx={{ gap: 0.5 }}>
      {!includeUnselectedLevel && (
        <FilterLabel id="subassortments" htmlFor="subassortments">
          {t('myAssortment.columns.subAssortments')}
        </FilterLabel>
      )}
      <Select
        id="subassortments"
        labelId="subassortments"
        size="small"
        sx={{ minWidth: 180, '& .MuiSelect-select': { whiteSpace: 'normal' } }}
        multiple
        value={
          selectedTagIds.length > 0 ? selectedTagIds : includeUnselectedLevel ? ['base'] : ['']
        }
        renderValue={(selected) =>
          selectedTagIds.length > 0 && selectedTags ? (
            <Stack flexDirection="row" flexWrap="wrap" gap={0.5}>
              {selected.map((tagId) => (
                <SubAssortmentChip
                  key={tagId}
                  small
                  tagName={selectedTags?.find((tag) => tag.id === tagId)!.name}
                />
              ))}
            </Stack>
          ) : includeUnselectedLevel ? (
            t('myAssortment.centralAssortments.assortmentType.base')
          ) : (
            <Typography variant="body2" fontWeight={500} color="textDisabled">
              {placeholderText}
            </Typography>
          )
        }
      >
        {!includeUnselectedLevel && (
          <MenuItem disabled value={''}>
            <Typography variant="body2">{placeholderText}</Typography>
          </MenuItem>
        )}
        {includeUnselectedLevel && (
          <>
            <MenuItem key="base" value="base" onClick={resetTags}>
              {t('myAssortment.centralAssortments.assortmentType.base')}
            </MenuItem>
            <ListSubheader sx={{ pt: '0 !important' }}>
              {t('myAssortment.centralAssortments.assortmentType.underAssortment')}
            </ListSubheader>
          </>
        )}

        {assortmentTags.map((tag) => (
          <MenuItem
            key={tag.id}
            value={tag.id}
            sx={{ py: 1.5 }}
            onClick={() => selectTagById(tag.id)}
          >
            <Checkbox checked={selectedTagIds?.includes(tag.id)} />
            <ListItemText primary={tag.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
